import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Home from "@arcgis/core/widgets/Home";
import Locate from "@arcgis/core/widgets/Locate";
import BasemapToggle from "@arcgis/core/widgets/BasemapToggle";
import Legend from "@arcgis/core/widgets/Legend";
import Expand from "@arcgis/core/widgets/Expand";
import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useLoading, handleLoading } from "context/LoadingContext";
import handleRangedItems from "utils/handleRangedItems";
import createFilterQuery from "utils/createFilterQuery";
import addGeometryAndNamedValuesState from "utils/addGeometryAndNamedValuesState";
import addDomainNamedValuesToAttributes from "utils/addDomainNamedValuesToAttributes";
import handleCreateObjectWithInStatment from "utils/handleCreateObjectWithInStatment";
import { useEsriUrls } from "./esriUrlsContext";
import { useAuth } from "./auth";
import getEsriDataFromLayer from "./contextFunctions/getEsriDataFromLayer";

const mapContext = createContext();
// Setting custom name for the context which is visible on react dev tools
mapContext.displayName = "Map Context";

// reducer
function reducer(state, action) {
  switch (action.type) {
    case "INIT": {
      return { ...state, ...action.payload };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

async function setView(dispatch, view) {
  dispatch({
    type: "INIT",
    payload: { view },
  });
}

async function setWebMap(dispatch, webMap) {
  dispatch({
    type: "INIT",
    payload: { webMap },
  });
}

async function setGraphicsLayer(dispatch, graphicsLayer) {
  dispatch({
    type: "INIT",
    payload: { graphicsLayer },
  });
}

// context provider
// eslint-disable-next-line react/prop-types
function MapContextProvider({ children }) {
  const mapDiv = useRef(null);
  const toolContentRef = useRef(null);

  const [, loadingDispatch] = useLoading();
  const [esriUrls] = useEsriUrls();
  const [auth] = useAuth();
  // const { token, roles } = auth;

  const initialState = {
    graphicsLayer: null,
    view: null,
    webMap: null,
    mapDiv,
    toolContentRef,
    polesSelectionComponentMode: "jobZoneList",
    paletteTrackingComponentMode: "palettesList",
    munsLayer: null,
    govsLayer: null,
    localitiesLayer: null,
    unitsTable: null,
    palettesTable: null,
    palettesHistoryTable: null,
    unitsHistoryTable: null,
    installationTeamsLkpTable: null,
    transferReportTable: null,
    govsList: [],
    munsList: [],
    localitiesList: [],
    polesList: [],
    polesReportList: [],
    palettesList: [],
    installedUnits: [],
    removedUnits: [],
    unitsHistory: [],
    jobZonesList: [],
    originalLocalitiesList: [],
    transferReportList: [],
    munQuery: null,
    selectionGeometry: null,
    sketchLayer: null,
    bufferLayer: null,
    selectedJobZoneforDetails: null,
    jobZonesGraphicLayer: null,
    showDrawTools: false,
  };
  const [controller, dispatch] = useReducer(reducer, initialState);

  // get layers from the webmap by their titles
  const getLayersAndGovs = async ({ map }) => {
    handleLoading(loadingDispatch, true);
    try {
      const [polesLayer, munsLayer, jobZonesLayer, localitiesLayer] = [
        "Poles",
        "Municipalities",
        "Job Zones",
        "Localities",
      ].map((layerTitle) =>
        map.allLayers.find((layer) => layer.title === layerTitle)
      );
      const [
        palettesTable,
        palettesHistoryTable,
        unitsTable,
        unitsHistoryTable,
        installationTeamsLkpTable,
        transferReportTable,
      ] = [
        "Pallets",
        "Pallets History",
        "Units",
        "Units History",
        "Installation Teams Lookup",
        "Transfer Report",
      ].map((tableTitle) =>
        map.allTables.find((table) => table.title === tableTitle)
      );

      const govsRes = await getEsriDataFromLayer({
        featureLayer: munsLayer,
        returnDistinctValues: true,
        outFields: ["Governorate_Arabic_Name", "Governorate_English_Name"],
      });
      dispatch({
        type: "INIT",
        payload: {
          polesLayer,
          unitsTable,
          munsLayer,
          govsList: govsRes.res,
          palettesTable,
          palettesHistoryTable,
          unitsHistoryTable,
          installationTeamsLkpTable,
          jobZonesLayer,
          localitiesLayer,
          transferReportTable,
        },
      });
    } catch (error) {
      console.log(error);
    }
    handleLoading(loadingDispatch, false);
  };

  useEffect(() => {
    const sketchLayer = new GraphicsLayer();
    const bufferLayer = new GraphicsLayer();

    dispatch({
      type: "INIT",
      payload: {
        sketchLayer,
        bufferLayer,
      },
    });
  }, []);

  useEffect(() => {
    handleLoading(loadingDispatch, true);
    if (auth.token) {
      // esriId.registerToken(rInfo);
      (async function () {
        if (mapDiv.current) {
          /**
           * Initialize application
           */
          const initMap = new WebMap({
            portalItem: {
              id: "12b7271bf5224c78b07fb23f81861c01",
              // id: "0ee99d75615f489298117f3bcb66b256", // test
              portal: process.env.REACT_APP_SERVICE_PORTAL,
            },
          });

          const initView = new MapView({
            container: mapDiv.current,
            map: initMap,
          });

          const sketchGraphicsLayer = new GraphicsLayer();
          const jobZonesGraphicLayer = new GraphicsLayer();

          const homeWidget = new Home({
            view: initView,
          });

          const locateWidget = new Locate({
            view: initView,
          });

          const basemapToggle = new BasemapToggle({
            view: initView, // The view that provides access to the map's "streets-vector" basemap
            nextBasemap: "satellite", // Allows for toggling to the "hybrid" basemap
          });

          const legend = new Legend({
            view: initView,
          });

          initView.when(async () => {
            setView(dispatch, initView);
            setWebMap(dispatch, initMap);
            try {
              // create graphic layer
              const initGraphicsLayer = new GraphicsLayer();
              setGraphicsLayer(dispatch, initGraphicsLayer);
              const initBordersGraphicsLayer = new GraphicsLayer();
              // setGraphicsLayer(dispatch, initGraphicsLayer);
              // initMap.add(initMapImageLayer);
              initMap.add(jobZonesGraphicLayer);
              initMap.add(initGraphicsLayer);
              initMap.add(sketchGraphicsLayer);
              initMap.add(initBordersGraphicsLayer);

              initView.ui.add(homeWidget, "top-left");
              initView.ui.add(locateWidget, "top-left");
              initView.ui.add(basemapToggle, "bottom-left");

              const bgExpand = new Expand({
                view: initView,
                content: legend,
                expandTooltip: "Legend",
              });

              initView.ui.add(bgExpand, "top-left");

              await getLayersAndGovs({
                dispatch,
                map: initMap,
              });

              dispatch({
                type: "INIT",
                payload: {
                  bordersGraphicLayer: initBordersGraphicsLayer,
                  jobZonesGraphicLayer,
                },
              });
            } catch (error) {
              console.log(error);
            }
          });
        }
        handleLoading(loadingDispatch, false);
      })();
    }
  }, [esriUrls, auth.userDetails, auth.token]);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <mapContext.Provider value={value}>{children}</mapContext.Provider>;
}

// custom hook for using context
function useMap() {
  const context = useContext(mapContext);

  if (!context) {
    throw new Error("useMap should be used inside the MapContextProvider.");
  }

  return context;
}

// Context module functions

/**
 *
 * @param {object} dispatch
 * @param {object} data data to be filter based on it
 * @param {*} view
 * @param {*} fields
 * @param {*} mapLayer
 * @param {*} domains
 * @param {string} objName object name to be set with new filtered data
 */
async function setDataBasedOnFilter({
  dispatch,
  data,
  view,
  fields,
  featureLayer,
  domains,
  objName,
}) {
  const dataWithRangeHandled = handleRangedItems(data);
  const { objectWithoutIns, objectWithIn } =
    handleCreateObjectWithInStatment(dataWithRangeHandled);
  const query = createFilterQuery(objectWithoutIns, fields, objectWithIn);
  const result = await featureLayer.queryFeatures({
    where: query || `1=1`,
    outFields: "*",
    returnGeometry: true,
    outSpatialReference: view.spatialReference,
  });

  // extract attributes from features and add them to the new variable
  const res = addGeometryAndNamedValuesState(result.features, domains);
  dispatch({ type: "INIT", payload: { [objName]: res } });
}

async function updateAfterDelete({
  dispatch,
  objIdToDelete,
  data,
  targetName,
}) {
  const updatedData = data.filter((item) => item.OBJECTID !== objIdToDelete);

  dispatch({
    type: "INIT",
    payload: { [targetName]: updatedData },
  });
}

async function updateAfterEdit({
  dispatch,
  newObj,
  data,
  targetName,
  domains,
}) {
  const tempData = JSON.parse(JSON.stringify(data));
  const tempNewObj = JSON.parse(JSON.stringify(newObj));
  // add named domains to the new object
  let newObjWithNamedDomains = {};
  if (domains) {
    newObjWithNamedDomains = addDomainNamedValuesToAttributes(domains, newObj);
  }

  tempData.push({
    ...tempNewObj,
    ...newObjWithNamedDomains,
    CREATED_DATE: new Date().getTime(),
    CREATED_DATE_ISO: new Date().toISOString(),
    date: new Date().toISOString(),
  });

  dispatch({
    type: "INIT",
    payload: { [targetName]: tempData },
  });
}

function addNewItem({ dispatch, data, newObj, targetName }) {
  dispatch({
    type: "INIT",
    payload: { [targetName]: [newObj, ...data] },
  });
}

export {
  MapContextProvider,
  useMap,
  setView,
  setDataBasedOnFilter,
  updateAfterDelete,
  updateAfterEdit,
  addNewItem,
};
